import { useState, useEffect, useRef } from "react";
import { useAuth } from "../App";
import { Bell, Check, CheckCheck, Trash2, AlertTriangle, AlertCircle, Info, PartyPopper, X } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const severityConfig = {
  critical: { icon: AlertTriangle, color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/20" },
  warning: { icon: AlertCircle, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20" },
  info: { icon: Info, color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20" },
  success: { icon: PartyPopper, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20" },
};

export default function NotificationBell() {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Poll unread count
  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchCount = async () => {
    try {
      const res = await api.get("/notifications/count");
      setUnreadCount(res.data.unread_count);
    } catch {}
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await api.get("/notifications?limit=30");
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread_count);
    } catch {
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  const generateNotifications = async () => {
    try {
      const res = await api.post("/notifications/generate");
      if (res.data.generated > 0) {
        toast.success(`${res.data.generated} new alerts generated`);
        fetchNotifications();
      } else {
        toast.info("No new alerts — you're all caught up!");
      }
    } catch {
      toast.error("Failed to check for alerts");
    }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-read", { all: true });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const dismissNotification = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
      fetchCount();
    } catch {}
  };

  const handleOpen = () => {
    if (!open) {
      fetchNotifications();
      generateNotifications();
    }
    setOpen(!open);
  };

  const timeAgo = (isoStr) => {
    if (!isoStr) return "";
    const diff = Date.now() - new Date(isoStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell"
        onClick={handleOpen}
        className="relative p-2 rounded-lg hover:bg-white/5 transition-colors"
      >
        <Bell className="w-5 h-5 text-zinc-400" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          className="absolute right-0 top-12 w-96 max-h-[500px] bg-[#0A0A0A] border border-white/10 rounded-lg shadow-2xl z-50 flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <h3 className="text-sm font-semibold text-white">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  data-testid="mark-all-read-btn"
                  onClick={markAllRead}
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  <CheckCheck className="w-3 h-3" /> Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Notification list */}
          <div className="overflow-y-auto flex-1 max-h-[400px]">
            {loading && notifications.length === 0 ? (
              <div className="p-6 text-center text-zinc-500 text-sm">Checking for alerts...</div>
            ) : notifications.length === 0 ? (
              <div className="p-6 text-center text-zinc-500 text-sm">
                No notifications yet. We'll alert you about upcoming payments, budget overruns, and goal milestones.
              </div>
            ) : (
              notifications.map((n) => {
                const config = severityConfig[n.severity] || severityConfig.info;
                const Icon = config.icon;
                return (
                  <div
                    key={n.id}
                    data-testid={`notification-${n.id}`}
                    className={`px-4 py-3 border-b border-white/5 flex gap-3 transition-colors ${
                      n.read ? "opacity-60" : "bg-white/[0.02]"
                    }`}
                  >
                    <div className={`mt-0.5 w-8 h-8 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-4 h-4 ${config.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-white truncate">{n.title}</p>
                        <button
                          onClick={() => dismissNotification(n.id)}
                          className="text-zinc-600 hover:text-red-400 flex-shrink-0"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-[10px] text-zinc-600 mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.read && <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
